import { useEffect, useId, useRef, useState } from 'react';

import { listReference } from '../../lib/api';

export interface PickerOption {
  code: string;
  label: string;
}

interface CodePickerProps<T> {
  label: string;
  /** The reference resource searched as the agent types: "airlines". */
  resource: string;
  value: string;
  onChange: (code: string) => void;
  /** Called with the whole item when one is picked from the list. */
  onPick?: (item: T) => void;
  toOption: (item: T) => PickerOption;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  disabled?: boolean;
}

/**
 * A code field with a searchable dropdown. Typing searches the reference
 * table by code or name; the field still takes a typed code as it is, so an
 * agent who knows it never has to open the list.
 */
export function CodePicker<T>({
  label,
  resource,
  value,
  onChange,
  onPick,
  toOption,
  placeholder,
  maxLength,
  error,
  disabled,
}: CodePickerProps<T>) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      listReference<T>(resource, { search: query, limit: 20 }, controller.signal)
        .then((result) => {
          setItems(result.items);
          setActive(0);
        })
        .catch(() => {
          if (!controller.signal.aborted) setItems([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, resource]);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (item: T) => {
    onChange(toOption(item).code);
    onPick?.(item);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && open && items[active]) {
      event.preventDefault();
      pick(items[active]);
    } else if (event.key === 'Escape' && open) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      setOpen(false);
    }
  };

  const listId = `${id}-list`;

  return (
    <div className="picker">
      <label className={`spark-input${error ? ' spark-input--invalid' : ''}`} htmlFor={id}>
        <span className="spark-label">{label}</span>
        <input
          id={id}
          className="spark-input__field picker__field"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && items[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          placeholder={placeholder}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          onFocus={() => {
            setQuery(value);
            setOpen(true);
          }}
          onBlur={() => setOpen(false)}
          onChange={(event) => {
            const typed = event.target.value.toUpperCase();
            onChange(typed);
            setQuery(typed);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="picker__caret" aria-hidden="true">
          ▾
        </span>
      </label>
      {error && (
        <span className="spark-input__error" role="alert">
          {error}
        </span>
      )}
      {open && (
        <ul className="picker__list" id={listId} role="listbox" ref={listRef}>
          {items.length === 0 && <li className="picker__empty">{loading ? 'Searching…' : 'No matches'}</li>}
          {items.map((item, i) => {
            const option = toOption(item);
            return (
              <li
                key={`${option.code}-${i}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={`picker__option${i === active ? ' picker__option--active' : ''}`}
                // mousedown, not click: the input's blur would close the list first.
                onMouseDown={(event) => {
                  event.preventDefault();
                  pick(item);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="picker__code">{option.code}</span>
                <span className="picker__label">{option.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
